import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import moment from 'moment';
import useHandleErrors from '../../utils/user.utils';
import { toast } from 'react-toastify';
import { useUser } from '../../utils/userContext';


const ChartComponent = () => {
    const [chartData, setChartData] = useState([]);
    const [totalHours, setTotalHours] = useState(0);
    const [daysWithHours, setDaysWithHours] = useState(0);
    const token = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const { user } = useUser();
    const hasFetchedRef = useRef(false);


    useEffect(() => {
        const fetchData = async () => {
            try {
                const path = `/viewTimesheet?employeeId=${user?.EmployeeId}`; // Adjust endpoint and parameters as needed
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    const timesheetData = response?.data?.listTimesheetDetails;
                    const data = prepareChartData(timesheetData);
                    setChartData(data);
                } else {
                    handleErrors(response)
                    console.error('Failed to fetch timesheet data:', response.statusText);
                }
            } catch (error) {
                console.error('Error fetching timesheet data:', error);
                if (error.response) {
                    if (error.response.status === 404) {
                        // Handle 404 status code separately
                        setChartData([0]);
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            }
        };
        if (!hasFetchedRef.current) {
            fetchData();
            hasFetchedRef.current = true;
        }
    }, []);

    const prepareChartData = (timesheetData) => {
        const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        const actualData = {
            x: daysOfWeek,
            y: [0, 8, 8, 8, 8, 8, 0], // Example data for 'Actual' (adjust as needed)
            type: 'bar',
            name: 'Actual',
            marker: {
                color: '#004F7C',
                line: {
                    width: 1,
                    color: '#004F7C',
                },
                // Removed unnecessary border configuration
            },
        };

        const loggedData = {
            x: daysOfWeek,
            y: calculateLoggedHours(timesheetData), // Calculate logged hours dynamically
            type: 'bar',
            name: 'Logged',
            marker: {
                color: '#25B740',
                line: {
                    width: 1,
                    color: '#25B740',
                },
                // Removed unnecessary border configuration
            },
        };

        return [actualData, loggedData];
    };

    const calculateLoggedHours = (timesheetData) => {
        const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const loggedHours = [0, 0, 0, 0, 0, 0, 0]; // Initialize array for each day of the week

        const today = moment(); // Get today's date
        const lastWeekStart = today.clone().subtract(1, 'week').startOf('week'); // Get last week's start date
        const lastWeekEnd = today.clone().subtract(1, 'week').endOf('week'); // Get last week's end date

        const previousWeekTimesheet = timesheetData[0].timesheetSummary.find((summary) => {
            const weekStartDate = moment(summary.weekStartDate);
            const weekEndDate = moment(summary.weekEndDate);
            return weekStartDate.isSameOrAfter(lastWeekStart) && weekStartDate.isSameOrBefore(lastWeekEnd) &&
                weekEndDate.isSameOrAfter(lastWeekStart) && weekEndDate.isSameOrBefore(lastWeekEnd);
        });

        if (previousWeekTimesheet) {
            previousWeekTimesheet.timeSheetHours.forEach((entry) => {
                const entryDate = moment(entry.weekDate); // Parse entry date
                const dayIndex = getDayIndex(entryDate.format("YYYY-MM-DD"));
                if (dayIndex !== -1 && entry.projectHours.length > 0) {
                    entry.projectHours.forEach((projectHour) => {
                        loggedHours[dayIndex] += projectHour.hours; // Accumulate hours for the corresponding day
                    });
                }
            });
        } else {
            console.log("No timesheet summary found for previous week");
        }

        // Calculate total hours and number of days with logged hours
        const totalLoggedHours = loggedHours.reduce((total, hours) => total + hours, 0);
        const daysWithLoggedHours = loggedHours.filter(hours => hours > 0).length;

        // Update state with the calculated values
        setTotalHours(totalLoggedHours);
        setDaysWithHours(daysWithLoggedHours);

        return loggedHours;
    };

    const getDayIndex = (weekDate) => {
        const dateObj = new Date(weekDate); // Parse your date string to Date object
        const dayIndex = dateObj.getDay(); // Get day index (0 for Sunday, 1 for Monday, etc.)

        return dayIndex !== -1 ? dayIndex : 0; // Default to Sunday if unable to determine day index
    };

    const layout = {
        font: {
            family: 'Arial',
            size: 13,
            color: '#212529',
        },
        xaxis: {
            tickangle: 0,
            fixedrange: true,
        },
        yaxis: {
            range: [0, 12],
            dtick: 1,
            showgrid: false,
            zeroline: true,
            showline: false,
            zerolinecolor: '#f1f1f3',
            zerolinewidth: 1,
            title: 'Hours',
            gridwidth: 1,
            linecolor: '#ddd',
            linewidth: 1,
            tickfont: { color: '#212529' },
            fixedrange: true,
        },
        legend: {
            orientation: 'h',
            x: 0.25,
            y: -0.25,
            itemclick: false,
            itemdoubleclick: false,
        },
        barcornerradius: 15,
        barmode: 'group',
        bargap: 0.6,
        bargroupgap: 0.2,
        automargin: true,
        paper_bgcolor: 'rgba(255,255,255,0)',
        plot_bgcolor: 'rgba(255,255,255,0)',
        margin: { l: 40, r: 30, t: 10, b: 0, pad: 5 },
        height: 300, // Added height property
    };

    return (
        <div className="chart-container">
            <div className="card-header">
                <div className="d-flex align-items-top justify-content-between">
                    <h5 className="mb-0 title">Weekly Work Hours Overview</h5>
                    <div className="hrs-count">
                        <span className="hrs-count-value">{totalHours}</span>
                        <small className="font-size-12 text-uppercase">{daysWithHours}Day<sup>(S)</sup></small>
                    </div>
                </div>
            </div>
            <Plot
                data={chartData}
                layout={layout}
                useResizeHandler
                style={{ width: '100%' }}
                config={{ responsive: true, displayModeBar: false }}
            />
        </div>
    );
};

export default ChartComponent;
